// Direct client for Viewrail's Frontastic BFF.
// Adds items to a cart on the BFF, then hands the session off to
// shop.viewrail.com via a one-click bookmarklet that the user installs once.

const BFF_ENDPOINT = "https://ecommdev-viewrailwebsite.frontastic.live";
const EXTENSION_VERSION = '"9cd588a"';
const SESSION_KEY = "viewrail-frontastic-session";

const HEADERS_BASE: Record<string, string> = {
  "Content-Type": "application/json",
  Accept: "application/json",
  "X-Frontastic-Access-Token": "APIKEY",
  "Frontastic-Locale": "en_US@USD",
  "Frontastic-Currency": "USD",
  "Commercetools-Frontend-Extension-Version": EXTENSION_VERSION,
};

// Recommended upsell — 1/8" Express Installation Kit (cable hardware).
// Surfaced in the UI as an opt-in checkbox; if selected, callers add
// this name to the items list passed to sendCartToViewrail.
export const INSTALL_KIT = {
  name: "Express Installation Kit",
  sku: "666122",
  price: 199.49,
  blurb: 'Hardware to terminate and tension 1/8" cables — one per run.',
} as const;

// Each shopping-list item maps to a Viewrail variant SKU. Posts default
// to the Standard Black 36" variant; cables and the install kit map
// directly. Anything not in this table is surfaced back to the caller
// as "skipped — add manually".
const SKU_BY_NAME: Record<string, string> = {
  "Level End Post": "738548",
  "Level Line Post": "738549",
  "Level Corner Post": "738554",
  "Angle End Post": "738556",
  "Angle Line Post": "738557",
  '1/8" Cable (100ft roll)': "666058",
  '1/8" Cable (400ft roll)': "653839",
  [INSTALL_KIT.name]: INSTALL_KIT.sku,
};

export type ViewrailLineRequest = { name: string; qty: number };

export type ViewrailAddResult = {
  cartId?: string;
  cartTotalCents?: number;
  added: { name: string; sku: string; qty: number }[];
  skipped: { name: string; qty: number; reason: string }[];
  handoffUrl: string; // open in new tab; user clicks the bookmarklet there
};

function getSession(): string {
  try {
    return localStorage.getItem(SESSION_KEY) || "";
  } catch {
    return "";
  }
}

function setSession(token: string) {
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
  } catch {
    // localStorage might be unavailable — proceed without persistence
  }
}

async function callBff(action: string, payload: unknown): Promise<{
  ok: boolean;
  status: number;
  body: unknown;
}> {
  const headers: Record<string, string> = { ...HEADERS_BASE };
  const sess = getSession();
  if (sess) headers["Frontastic-Session"] = sess;

  const res = await fetch(`${BFF_ENDPOINT}/frontastic/action/${action}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload ?? {}),
  });

  const newSess = res.headers.get("Frontastic-Session");
  if (newSess) setSession(newSess);

  const text = await res.text();
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    body = text;
  }
  return { ok: res.ok, status: res.status, body };
}

export function lookupSku(name: string): string | null {
  return SKU_BY_NAME[name] ?? null;
}

// Submits each item to the BFF as a separate cart/addToCart call. Items
// without a SKU mapping (e.g. cable rolls) are skipped — caller can show
// them as "added separately at viewrail.com" if desired.
export async function sendCartToViewrail(
  items: ViewrailLineRequest[],
): Promise<ViewrailAddResult> {
  const added: ViewrailAddResult["added"] = [];
  const skipped: ViewrailAddResult["skipped"] = [];

  type CartShape = { cartId?: string; sum?: { centAmount?: number } };
  let lastCart: CartShape | null = null;

  for (const item of items) {
    if (item.qty <= 0) continue;
    const sku = lookupSku(item.name);
    if (!sku) {
      skipped.push({ name: item.name, qty: item.qty, reason: "no SKU mapping" });
      continue;
    }
    const r = await callBff("cart/addToCart", {
      variant: { sku, count: item.qty },
    });
    if (!r.ok) {
      skipped.push({
        name: item.name,
        qty: item.qty,
        reason: `BFF ${r.status}`,
      });
      continue;
    }
    added.push({ name: item.name, sku, qty: item.qty });
    if (r.body && typeof r.body === "object") {
      lastCart = r.body as CartShape;
    }
  }

  // The handoff URL drops the freshest session token in the URL fragment.
  // The bookmarklet (installed once on the user's bookmarks bar) reads
  // the fragment, plants the cookie on shop.viewrail.com, and redirects
  // to /cart. The token lives in the fragment only, so it never hits any
  // server log.
  const session = getSession();
  const handoffUrl = `https://shop.viewrail.com/cart/#__viewrail_token=${encodeURIComponent(session)}`;

  return {
    cartId: lastCart?.cartId,
    cartTotalCents: lastCart?.sum?.centAmount,
    added,
    skipped,
    handoffUrl,
  };
}

export function clearViewrailSession() {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

// Static bookmarklet — never embeds a session token. Always reads the
// token from `location.hash` (set by sendCartToViewrail's handoffUrl).
// User drags this to their bookmarks bar exactly once.
export const VIEWRAIL_BOOKMARKLET_HREF = `javascript:${encodeURIComponent(
  `(function(){
    var m = location.hash.match(/__viewrail_token=([^&]+)/);
    if (!m) {
      alert('No cart token in URL. Click "Send to Viewrail" in the design app first.');
      return;
    }
    var t = decodeURIComponent(m[1]);
    if (!/(^|\\.)viewrail\\.com$/.test(location.hostname)) {
      alert('Run this on shop.viewrail.com.');
      return;
    }
    var maxAge = 60 * 60 * 24 * 7;
    document.cookie = 'frontastic-session=' + t + '; path=/; secure; SameSite=Lax; Max-Age=' + maxAge;
    location.href = '/cart';
  })();`.replace(/\s+/g, " ").trim(),
)}`;
